import React, { useState, useEffect } from 'react';
import theme from '@/data/theme';
import Modal from 'react-modal';
import { SeatAPIModel } from '@/apiModels/SeatReservation';
import {
  LabelInputWrapper,
  StyledBottomButtonWrapper,
  StyledFilledBorderButton,
  StyledInputField,
  StyledInputLabel,
  StyledModalHeader,
  StyledModalTitleWrapper,
  StyledSavedButton,
} from '@/components/ui/Modals/EditTableModal/EditTableModal.styled.ts';
import useUpdateEventSeat from '@/hooks/useUpdateEventSeat.ts';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import RichTextEditor from '@/components/ui/RichTextEditor';
import ModalCloseButton from '@/components/ui/ModalCloseButton';
import LoadingSplash from '@/components/ui/LoadingScreen';

interface EditTableProps {
  isOpen: boolean;
  onClose: () => void;
  seatInformation: SeatAPIModel;
  seatId: string;
  eventId: string;
  isLoading: boolean;
}

const EditTableModal: React.FunctionComponent<EditTableProps> = ({
  isOpen,
  onClose,
  seatInformation,
  seatId,
  eventId,
  isLoading,
}) => {
  const handleModalClose = () => {
    onClose();
  };

  const [editedPrice, setEditedPrice] = useState<string>(seatInformation.price || '');
  const [editedPax, setEditedPax] = useState<number>(seatInformation.pax || 0);
  const [editedDetails, setEditedDetails] = useState<string>(seatInformation.features || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ pax?: string; price?: string }>({});

  useEffect(() => {
    setEditedPrice(seatInformation.price || '');
    setEditedPax(seatInformation.pax || 0);
    setEditedDetails(seatInformation.features || '');
    setErrors({});
  }, [seatInformation, isOpen]);

  const queryClient = useQueryClient();

  const { mutate: updateSeatStatus } = useUpdateEventSeat({
    cb: () => {},
    cbSuccess: () => {
      toast.success(`You have successfully edited table : ${seatInformation.serial_no}`);
      queryClient.invalidateQueries({
        queryKey: ['seat'],
      });
      setIsSubmitting(false);
      onClose();
    },
    cbError: (error) => {
      const errors = error?.response?.data?.errors || [];
      errors.forEach((e) => {
        if (e.field === 'price') {
          setErrors((prev) => ({ ...prev, price: e.message[0] }));
        }
        if (e.field === 'pax') {
          setErrors((prev) => ({ ...prev, pax: e.message[0] }));
        }
      });

      setIsSubmitting(false);
    },
  });

  const validateForm = (): boolean => {
    const newErrors: { pax?: string; price?: string } = {};

    if (!editedPax || editedPax <= 0) {
      newErrors.pax = 'Pax must be greater than 0';
    }

    if (!editedPrice || parseFloat(editedPrice) <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveChanges = () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setIsSubmitting(true);
    updateSeatStatus({
      eventId,
      seatId,
      payload: {
        price: editedPrice,
        pax: editedPax,
        features: editedDetails,
      },
    });
  };

  const handlePaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.valueAsNumber;
    setEditedPax(value);
    if (value > 0) {
      setErrors((prev) => ({ ...prev, pax: undefined }));
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEditedPrice(value);
    if (value && parseFloat(value) > 0) {
      setErrors((prev) => ({ ...prev, price: undefined }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick={false}
      onRequestClose={handleModalClose}
      style={{
        content: {
          maxWidth: '360px',
          width: 'clamp(320px, 8.125vw + 294px, 450px)',
          minWidth: '296px',
          height: 'fit-content',
          borderRadius: '10px',
          padding: '25px 10px',
          inset: '0',
          margin: 'auto',
          boxShadow: '0px 2px 6px 0px rgba(134, 92, 208, 0.50)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          fontFamily: 'Poppins',
        },
        overlay: {
          background: theme.colors.modalBackground,
        },
      }}
    >
      <LoadingSplash
        isVisible={isSubmitting}
        text={`Editing Table ${seatInformation.serial_no}...`}
        spinnerColor={theme.colors.primary}
        backgroundColor="rgba(255, 255, 255, 0.95)"
        textColor={theme.colors.primary}
        spinnerSize="40px"
        borderRadius="10px"
      />
      <StyledModalTitleWrapper>
        <StyledModalHeader>Edit Table: {seatInformation.serial_no}</StyledModalHeader>
        <ModalCloseButton onClose={handleModalClose} />
      </StyledModalTitleWrapper>

      <LabelInputWrapper>
        <StyledInputLabel>Pax</StyledInputLabel>
        <StyledInputField
          type="number"
          placeholder={'Enter the number of Pax'}
          value={editedPax}
          onChange={handlePaxChange}
          style={{
            borderColor: errors.pax ? '#cc3333' : undefined,
          }}
        />
        {errors.pax && (
          <div
            style={{
              color: '#cc3333',
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {errors.pax}
          </div>
        )}
      </LabelInputWrapper>

      <LabelInputWrapper>
        <StyledInputLabel>Total Amount</StyledInputLabel>
        <StyledInputField
          type="text"
          placeholder={'Enter price of the table'}
          value={editedPrice}
          onChange={handlePriceChange}
          style={{
            borderColor: errors.price ? '#cc3333' : 'none',
          }}
        />
        {errors.price && (
          <div
            style={{
              color: '#cc3333',
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {errors.price}
          </div>
        )}
      </LabelInputWrapper>

      <LabelInputWrapper>
        <StyledInputLabel>Details</StyledInputLabel>
        <RichTextEditor
          value={editedDetails}
          onChange={(e) => setEditedDetails(e.target.value)}
          name={'details'}
        />
      </LabelInputWrapper>

      <StyledBottomButtonWrapper>
        <StyledFilledBorderButton onClick={onClose} disabled={isLoading || isSubmitting}>
          Cancel
        </StyledFilledBorderButton>
        <StyledSavedButton onClick={handleSaveChanges} disabled={isLoading || isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </StyledSavedButton>
      </StyledBottomButtonWrapper>
    </Modal>
  );
};

export default EditTableModal;
